import type { OperatorStepAction } from '@erp/shared-types';
import type { useGT } from 'gt-react';
import { actionDetail } from 'src/components/common/agentActionFormatting';

/** Labels describe recorded actions, never infer what a coordinate targeted. */
export function operatorActionTitle(
  action: OperatorStepAction,
  gt: ReturnType<typeof useGT>,
): string {
  const args = action.args;
  const name = typeof args?.action === 'string' ? args.action : action.tool;
  if (name === 'navigate' && typeof args?.url === 'string')
    return gt('Opened {destination}', {
      context: 'recorded browser navigation',
      destination: navigationDestination(args.url),
    });
  if (name === 'key')
    return gt('Pressed {key}', {
      context: 'recorded keyboard action',
      key: stringArg(args?.text ?? args?.key),
    });
  if (name === 'type')
    return gt('Typed “{text}”', {
      context: 'recorded text input',
      text: stringArg(args?.text),
    });
  if (name === 'screenshot')
    return gt('Captured screenshot', { context: 'recorded browser action' });
  if (name === 'left_click')
    return gt('Clicked {target}', {
      context: 'recorded click without inferred target',
      target: coordinateLabel(args?.coordinate),
    });
  if (name === 'mouse_move')
    return gt('Moved pointer to {target}', {
      context: 'recorded pointer movement',
      target: coordinateLabel(args?.coordinate),
    });
  return [
    name.replaceAll('_', ' '),
    actionDetail(name, action.args ?? undefined),
  ]
    .filter(Boolean)
    .join(' · ');
}

function coordinateLabel(value: unknown): string {
  return Array.isArray(value) ? `(${value.join(', ')})` : '';
}

function navigationDestination(value: string): string {
  try {
    const url = new URL(value);
    if (
      url.hostname.endsWith('.wikipedia.org') &&
      url.pathname.startsWith('/wiki/')
    )
      return decodeURIComponent(url.pathname.slice(6)).replaceAll('_', ' ');
    return url.hostname;
  } catch {
    return value;
  }
}

function stringArg(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
